/**
 * Everything a trading plan is worth, derived — because none of it is stored.
 *
 * The journal stores intent and fills. Realised P&L, R multiples, position size,
 * average entry, equity and danger are all computed here, at read time, from the
 * rows the server actually keeps. The reason is one line in the plan doc and it
 * governs this whole file: *the executions are the fact, and a stored total
 * becomes a second answer the moment a fill is corrected — and the stored one is
 * the one that is wrong.*
 *
 * It lives in the client because it is the same arithmetic a form needs **while
 * you type**. A server round trip per keystroke to learn what 2% of the account
 * buys would make the form feel broken, and having the number in two places is
 * how they drift.
 *
 * Pure and tested in Node, like the chart's render-plan layer: nothing here
 * touches React, a URL, or a clock it did not receive.
 *
 * ## One R is fixed at the fill, and that is the load-bearing rule
 *
 * Every R here measures from the **frozen** stop (`plan.planned.stop`), never
 * the live one. If 1R tracked the current stop, a trailed trade would drift
 * toward +infinity R and no two rows in the journal would be comparable — the
 * risk column reads the frozen stop and the stop column is live, and they are
 * deliberately different numbers.
 *
 * Before the freeze there is no `planned`, so the plan's own stop *is* the
 * intent and is what sizing uses. That is not an exception to the rule; it is
 * the same rule at a moment when the two are equal by definition.
 */

import type { Execution, Plan, Target, Trade } from "@qufolio/model";

/** The stop 1R is measured from: the frozen one once there is one. */
export function riskStop(plan: Plan): number {
  return plan.planned?.stop ?? plan.stop;
}

/** The entry 1R is measured from, for the same reason. */
export function riskEntry(plan: Plan): number {
  return plan.planned?.entry ?? plan.entry;
}

/**
 * What one share risks — the distance from entry to the stop that defines 1R.
 *
 * Absolute, so direction never has to be consulted: a short's stop sits above
 * its entry and the distance is the same positive number either way. Direction
 * decides the *sign of the outcome*, which is `rMultiple`'s business.
 */
export function riskPerShare(plan: Plan): number {
  return Math.abs(riskEntry(plan) - riskStop(plan));
}

/**
 * How many shares put `plan.risk` at stake, floored to whole ones.
 *
 * Floored rather than rounded: rounding up risks more than the plan said, and a
 * plan whose whole point is a fixed risk must not quietly exceed it. Zero when
 * the stop is at the entry — that is not a position of infinite size, it is a
 * plan with no risk defined yet, and the form should say so rather than render
 * `Infinity`.
 */
export function positionSize(plan: Plan): number {
  const perShare = riskPerShare(plan);
  return perShare > 0 ? Math.floor(plan.risk / perShare) : 0;
}

/** What the position costs to open, at the planned entry. */
export function positionCost(plan: Plan): number {
  return positionSize(plan) * riskEntry(plan);
}

/**
 * Where a price sits in R, signed by the plan's direction.
 *
 * `+2` means two times the planned risk in your favour, for a long *and* for a
 * short. That is the whole point of R as a unit: it makes a short and a long
 * comparable rows in one journal, which raw prices never are.
 */
export function rMultiple(plan: Plan, price: number): number {
  const perShare = riskPerShare(plan);
  if (perShare <= 0) return 0;
  const move = price - riskEntry(plan);
  return (plan.direction === "long" ? move : -move) / perShare;
}

/**
 * The plan's targets with their R, in order.
 *
 * Uses the **live** targets, because a target is editable after the fill and
 * the question "where am I going" is about now. The frozen ones are what
 * `plannedR` answers against, which is a different question.
 */
export function targetR(plan: Plan): { target: Target; r: number }[] {
  return (plan.targets ?? []).map((target) => ({ target, r: rMultiple(plan, target.price) }));
}

/**
 * The blended R this plan was aiming at — its share-weighted expectation.
 *
 * **Not the first target's R, and not the furthest.** A 50/50 split at +2.00R
 * and +3.93R is a 2.97R plan, and calling it either 2.00R or 3.93R makes the
 * plan and its outcome incomparable the moment anyone scales out — which is the
 * entire reason a target carries a share rather than a quantity.
 *
 * Weighted by the **frozen** targets once there are any, so the number a
 * finished trade is judged against is the one that was actually planned rather
 * than whatever the targets were last dragged to.
 */
export function plannedR(plan: Plan): number {
  const targets = plan.planned?.targets ?? plan.targets ?? [];
  if (targets.length === 0) return 0;
  const total = targets.reduce((sum, t) => sum + t.share, 0);
  if (total <= 0) return 0;
  return targets.reduce((sum, t) => sum + t.share * rMultiple(plan, t.price), 0) / total;
}

/**
 * Net position from a set of fills: signed quantity and the average price paid.
 *
 * Signed, so a long is positive and a short negative, and a fully closed
 * position is exactly zero rather than "the buys happen to equal the sells".
 *
 * The average is over the **opening** side only. Averaging every fill together
 * would blend exits into the entry price and make a scaled-out winner look like
 * it was bought higher than it was.
 */
export function netPosition(
  executions: readonly Execution[],
  direction: Plan["direction"] = "long",
): { quantity: number; averagePrice: number } {
  const opening = direction === "long" ? "buy" : "sell";
  let signed = 0;
  let cost = 0;
  let opened = 0;
  for (const fill of executions) {
    const sign = fill.side === "buy" ? 1 : -1;
    signed += sign * fill.quantity;
    if (fill.side === opening) {
      cost += fill.quantity * fill.price;
      opened += fill.quantity;
    }
  }
  return { quantity: signed, averagePrice: opened > 0 ? cost / opened : 0 };
}

/**
 * Realised money, from the fills alone.
 *
 * Fees are subtracted when present and ignored when absent, which is the honest
 * reading of an optional field — a missing fee is unknown, not zero, but a
 * journal that refused to total until every fee was entered would be unusable.
 * See the open question in the plan doc about whether P&L should be true to a
 * broker statement or true to the trade idea; this is currently the latter.
 */
export function realised(
  executions: readonly Execution[],
  direction: Plan["direction"] = "long",
): number {
  const closing = direction === "long" ? "sell" : "buy";
  const { averagePrice } = netPosition(executions, direction);
  let total = 0;
  for (const fill of executions) {
    if (fill.side === closing) {
      const move = fill.price - averagePrice;
      total += (direction === "long" ? move : -move) * fill.quantity;
    }
    total -= fill.fees ?? 0;
  }
  return total;
}

/** Realised money as a multiple of what the plan risked. */
export function realisedR(plan: Plan, executions: readonly Execution[]): number {
  const perShare = riskPerShare(plan);
  const size = positionSize(plan);
  const oneR = perShare * size;
  return oneR > 0 ? realised(executions, plan.direction) / oneR : 0;
}

/** Money at stake right now, if the live stop were hit on the whole position. */
export function openRisk(plan: Plan, executions: readonly Execution[]): number {
  const { quantity, averagePrice } = netPosition(executions, plan.direction);
  const held = Math.abs(quantity);
  if (held === 0) return 0;
  const move = plan.direction === "long" ? averagePrice - plan.stop : plan.stop - averagePrice;
  // Negative means the stop is already past the average entry — the risk is
  // retired and this is locked-in profit, not a debt. Clamped, because "risk"
  // that is a negative number reads as a credit in a heat total.
  return Math.max(0, move * held);
}

/**
 * Danger, and it has **two** sources that do not collapse into one number.
 *
 * - **Price danger** — a live position that has given back three quarters of
 *   its distance to the stop.
 * - **Time danger** — a position that is *winning* and still carrying its whole
 *   original risk, because nothing has moved the stop up.
 *
 * Collapsing these hides the second entirely: a position six days old at
 * +1.42R with an untouched stop looks perfectly healthy by price. Neither is a
 * status anybody sets — both appear and clear with the market, which is why
 * nothing is stored.
 */
export interface Danger {
  price: boolean;
  time: boolean;
}

/** How much of the way to the stop counts as giving it back. */
export const PRICE_DANGER_FRACTION = 0.75;

/** Days a position may carry its whole original risk before it is flagged. */
export const DEFAULT_TIME_LIMIT_DAYS = 5;

export function danger(
  plan: Plan,
  executions: readonly Execution[],
  price: number,
  now: Date = new Date(),
): Danger {
  const { quantity } = netPosition(executions, plan.direction);
  if (Math.abs(quantity) === 0) return { price: false, time: false };

  const entry = riskEntry(plan);
  const stop = riskStop(plan);
  const perShare = Math.abs(entry - stop);

  // How far back toward the stop price has come, as a fraction of 1R.
  const givenBack = perShare > 0 ? (plan.direction === "long" ? entry - price : price - entry) / perShare : 0;

  // "Still at risk" means the *live* stop has not moved past the entry. Once it
  // has, the clock stops mattering — which is what makes the stop rule and the
  // time limit two halves of one idea rather than two features.
  const stillAtRisk =
    plan.direction === "long" ? plan.stop < entry : plan.stop > entry;

  const opened = executions.length > 0 ? new Date(executions[0]!.executedAt) : now;
  const days = Math.floor((now.getTime() - opened.getTime()) / 86_400_000);
  const limit = plan.timeLimitDays ?? DEFAULT_TIME_LIMIT_DAYS;

  return {
    price: givenBack >= PRICE_DANGER_FRACTION,
    time: stillAtRisk && days >= limit && rMultiple(plan, price) > 0,
  };
}

/**
 * Account equity: the starting balance plus what closed trades realised.
 *
 * Derived rather than stored because a fixed balance never notices a drawdown,
 * and noticing the drawdown is most of what paper trading is for. Only *closed*
 * trades count — an open position's paper gain is not money, and letting it
 * inflate the balance would size the next plan against a number that can
 * evaporate.
 */
export function equity(
  startingBalance: number,
  trades: readonly Trade[],
  executionsByTrade: ReadonlyMap<string, readonly Execution[]>,
): number {
  let total = startingBalance;
  for (const trade of trades) {
    if (!trade.closedAt) continue;
    total += realised(executionsByTrade.get(trade.id) ?? [], trade.direction);
  }
  return total;
}

/**
 * Where a newly added target goes, before anybody drags it.
 *
 * One R beyond the furthest one there already is, or 2R from the entry when
 * there are none. A default rather than a prompt: the point of adding a target
 * from the chart is that the next thing you do is *drag* it, and a dialog
 * between the two turns one gesture into three.
 *
 * Signed by direction, so a short's targets step downwards.
 */
export function nextTargetPrice(plan: Plan): number {
  const entry = riskEntry(plan);
  const perShare = riskPerShare(plan);
  if (perShare <= 0) return entry;

  const step = plan.direction === "long" ? perShare : -perShare;
  const targets = plan.targets ?? [];
  if (targets.length === 0) return round2(entry + step * 2);

  // The furthest *in the direction of the trade*, which for a short is the
  // lowest — sorting by price alone would step back towards the entry.
  const furthest = targets.reduce(
    (best, t) => ((plan.direction === "long" ? t.price > best : t.price < best) ? t.price : best),
    targets[0]!.price,
  );
  return round2(furthest + step);
}

/** Prices carry cents; more decimals here would be arithmetic noise. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Whether a target may be removed from the chart.
 *
 * **Only the last one, and never the only one.** A plan whose targets are T1
 * and T2 can lose T2; losing T1 instead would renumber the remaining exit and
 * leave "T1" meaning what T2 meant, which is a different plan wearing the same
 * label. And a plan with no target at all has a position that rides to the stop
 * with nothing to take it off.
 *
 * The rule generalises past the two the form seeds: the last is always the one
 * furthest out, so removing it is the only edit that leaves the rest meaning
 * exactly what they meant before.
 */
export function canRemoveTarget(plan: Plan, targetId: string): boolean {
  const targets = plan.targets ?? [];
  if (targets.length < 2) return false;
  return targets[targets.length - 1]!.id === targetId;
}
